import { openQuickAddModalForProductPage } from '@theme/quick-add';

const TILE_ROOT_SELECTORS = '.bunnings-product-card, .bunnings-product-tile';
const TILE_FORM_SELECTOR = '.bunnings-product-card__form, .bunnings-product-tile__form';
const TILE_TITLE_LINK_SELECTOR =
  '.bunnings-product-card__title-link, .bunnings-product-tile__title-link';

/**
 * Kiosk tiles use `data-kiosk-product` + chooser click capture in bunnings-kiosk-main; skip those here.
 * Everywhere else, multi-variant Bunnings cards (`data-bunnings-needs-variant-choice`) open the quick-add
 * dialog instead of posting the card's default variant.
 */
function findTileRoot(element) {
  if (!(element instanceof Element)) return null;
  return element.closest(TILE_ROOT_SELECTORS);
}

function cardProductPageUrl(card) {
  const titleLink = card.querySelector(
    '.bunnings-product-card__title-link[href], .bunnings-product-tile__title-link[href]'
  );
  if (titleLink instanceof HTMLAnchorElement) return titleLink.href;
  const mediaLink = card.querySelector(
    '.bunnings-product-card__media-link[href], .bunnings-product-tile__media-link[href]'
  );
  if (mediaLink instanceof HTMLAnchorElement) return mediaLink.href;
  return null;
}

function cardProductTitle(card) {
  const titleLink = card.querySelector(TILE_TITLE_LINK_SELECTOR);
  return (titleLink?.textContent || '').trim();
}

function openChooseQuickAddFromForm(form) {
  const card = findTileRoot(form);
  if (!card) return;
  if (!card.hasAttribute('data-bunnings-needs-variant-choice')) return;
  if (card.closest('[data-kiosk-product]')) return;

  const productUrl = cardProductPageUrl(card);
  if (!productUrl) return;

  const variantInput = form.querySelector('input[name="id"]');
  const presetVariantId = variantInput instanceof HTMLInputElement ? variantInput.value : undefined;
  const title = cardProductTitle(card);

  return openQuickAddModalForProductPage(productUrl, title, presetVariantId, { kioskAppearance: true });
}

function setChooseButtonLoading(button, isLoading) {
  if (!(button instanceof HTMLButtonElement)) return;
  button.dataset.loading = isLoading ? 'true' : 'false';
  button.disabled = isLoading;
  button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const ctaButton = target.closest('button.add-to-cart-button');
    if (!(ctaButton instanceof HTMLButtonElement)) return;
    const card = findTileRoot(ctaButton);
    if (!card?.hasAttribute('data-bunnings-needs-variant-choice')) return;
    if (card.closest('[data-kiosk-product]')) return;
    const form = ctaButton.closest(TILE_FORM_SELECTOR);
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();
    setChooseButtonLoading(ctaButton, true);
    Promise.resolve(openChooseQuickAddFromForm(form)).finally(() => {
      setChooseButtonLoading(ctaButton, false);
    });
  },
  true
);

/**
 * Standalone bunnings-add-to-cart-button snippet carries the product URL, title, and preset variant
 * on the button itself (no ancestor card/form). Route those clicks through the same quick-add modal.
 */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button[data-bunnings-choose-variant]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.closest('[data-kiosk-product]')) return;

    const productUrl = button.dataset.productUrl;
    if (!productUrl) return;
    const title = button.dataset.productTitle || '';
    const presetVariantId = button.dataset.variantId;

    event.preventDefault();
    event.stopPropagation();
    setChooseButtonLoading(button, true);
    Promise.resolve(
      openQuickAddModalForProductPage(productUrl, title, presetVariantId, { kioskAppearance: true })
    ).finally(() => {
      setChooseButtonLoading(button, false);
    });
  },
  true
);
